import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Package } from "lucide-react";
import { GOOGLE_SHEETS_URL, FORMSPREE_ENDPOINT } from "../data/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function trackEvent(name, params = {}) {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", name, params);
  }
}

function getUTMParams() {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  return {
    utm_source:   p.get("utm_source")   || "",
    utm_medium:   p.get("utm_medium")   || "",
    utm_campaign: p.get("utm_campaign") || "",
    utm_content:  p.get("utm_content")  || "",
    utm_term:     p.get("utm_term")     || "",
  };
}

export default function LeadForm({ source = "home_quick_form", prefill = {}, compact = false }) {
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    message: prefill.message || "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [utms, setUtms] = useState({});

  useEffect(() => {
    setUtms(getUTMParams());
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e, isSample = false) => {
    e.preventDefault();
    setStatus("loading"); setError("");
    const payload = {
      name: form.name,
      company: form.company,
      email: form.email,
      phone: form.phone,
      message: form.message,
      categories: (prefill.categories || (isSample ? ["Sample Request"] : [])).join(", "),
      type: isSample ? "Sample Request" : "Quote Request",
      source: isSample ? `${source}_sample` : source,
      page_url: typeof window !== "undefined" ? window.location.href : "",
      ...utms,
    };
    try {
      if (BACKEND_URL) {
        await axios.post(`${BACKEND_URL}/api/leads`, { ...payload, country: "", order_volume: "" });

      } else if (GOOGLE_SHEETS_URL) {
        // Fire Formspree first (email notification) — don't wait, don't block
        if (FORMSPREE_ENDPOINT) {
          fetch(FORMSPREE_ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json", "Accept": "application/json" },
            body: JSON.stringify(payload),
          }).catch(() => { /* silent */ });
        }
        // Apps Script GET — personal Gmail account, CORS confirmed working
        const qs = new URLSearchParams({
          name: payload.name, company: payload.company,
          email: payload.email, phone: payload.phone,
          message: payload.message, type: payload.type,
          source: payload.source, categories: payload.categories,
          utm_source: payload.utm_source || "", utm_medium: payload.utm_medium || "",
          utm_campaign: payload.utm_campaign || "", page_url: payload.page_url || "",
        }).toString();
        const res = await fetch(`${GOOGLE_SHEETS_URL}?${qs}`, { method: "GET" });
        let data = {};
        try { data = await res.json(); } catch (parseErr) { /* non-JSON is fine */ }
        if (!res.ok) throw new Error(`Submission failed (${res.status})`);
        if (data.ok === false) throw new Error(data.error || "Submission failed.");

      } else if (FORMSPREE_ENDPOINT) {
        const res = await fetch(FORMSPREE_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify(payload),
        });
        let data = {};
        try { data = await res.json(); } catch (parseErr) { /* non-JSON */ }
        if (!res.ok || data.error) throw new Error(data.error || `Submission failed (${res.status})`);

      } else {
        throw new Error("No endpoint configured. Please contact us via WhatsApp or email.");
      }
      setStatus("success");
      trackEvent("form_submission", {
        form_type: isSample ? "sample_request" : "quote_request",
        source,
        ...utms,
      });
      // Google Ads conversion — replace AW-XXXXXXX/XXXXXXX with your conversion tag
      // if (window.gtag) window.gtag("event", "conversion", { send_to: "AW-XXXXXXX/XXXXXXX" });
      // Meta Pixel conversion
      // if (window.fbq) window.fbq("track", "Lead");
    } catch (err) {
      setStatus("error");
      setError(err?.response?.data?.detail?.[0]?.msg || err?.message || "Submission failed. Please WhatsApp us or email export@indusart.in");
    }
  };

  if (status === "success") {
    return (
      <div data-testid="lead-form-success" className="rounded-3xl bg-white shadow-soft p-8 md:p-12 text-center">
        <div className="w-16 h-16 rounded-full bg-[#8a9a7b]/20 flex items-center justify-center mx-auto mb-6">
          <Check size={30} strokeWidth={2} className="text-[#8a9a7b]" />
        </div>
        <div className="font-serif text-3xl text-[#1e2a5a] mb-3">Received. Thank you.</div>
        <p className="text-[#1a1a1a]/70 leading-[1.7] mb-6 max-w-md mx-auto">
          A specialist from our export desk will respond within 24 hours with pricing, lead time, and a recommended path.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Link to="/our-craft" className="editorial-link text-sm font-semibold text-[#1e2a5a]">Explore our craft</Link>
          <Link to="/projects" className="editorial-link text-sm font-semibold text-[#1e2a5a]">See case studies</Link>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={(e) => submit(e, false)} data-testid="lead-form" className={`rounded-3xl bg-white shadow-soft ${compact ? "p-6 md:p-8" : "p-8 md:p-10"}`}>
      <div className="grid sm:grid-cols-2 gap-5 mb-5">
        <Field label="Name" required>
          <input data-testid="lead-name" required value={form.name} onChange={(e) => update("name", e.target.value)} className={inputCls} placeholder="Your full name" />
        </Field>
        <Field label="Company" required>
          <input data-testid="lead-company" required value={form.company} onChange={(e) => update("company", e.target.value)} className={inputCls} placeholder="Your company" />
        </Field>
        <Field label="Email" required>
          <input data-testid="lead-email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} placeholder="you@company.com" />
        </Field>
        <Field label="Phone (optional)">
          <input data-testid="lead-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} placeholder="+1 555 555 5555" />
        </Field>
      </div>

      <Field label="Your Requirements" required>
        <textarea data-testid="lead-message" required rows={4} value={form.message} onChange={(e) => update("message", e.target.value)} className={inputCls + " resize-none"} placeholder="Tell us about your project — category, volume, design path…" />
      </Field>

      {status === "error" && <div data-testid="lead-form-error" className="text-[#b4462a] text-sm mt-4">{error}</div>}

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <button
          type="submit"
          disabled={status === "loading"}
          data-testid="lead-submit"
          className="btn-pill bg-[#1e2a5a] text-[#faf6ef] hover:bg-[#b4462a] shadow-cta py-4 px-7 text-sm flex-1 sm:flex-none disabled:opacity-60"
        >
          {status === "loading" ? "Sending…" : "Submit"}
          <ArrowRight size={16} strokeWidth={2.2} />
        </button>
        <button
          type="button"
          onClick={(e) => submit(e, true)}
          disabled={status === "loading"}
          data-testid="lead-sample-request"
          className="btn-pill bg-[#faf6ef] text-[#1e2a5a] hover:bg-[#d98f2f] hover:text-[#faf6ef] border border-[#1e2a5a]/15 py-4 px-7 text-sm flex-1 sm:flex-none disabled:opacity-60"
        >
          <Package size={16} strokeWidth={2} />
          Request a Sample
        </button>
      </div>

      <div className="mt-5 text-xs text-[#1a1a1a]/55 leading-[1.7]">
        We respond to all inquiries within 24 hours. Your information is kept confidential.
      </div>
    </form>
  );
}

const inputCls = "w-full rounded-2xl bg-[#faf6ef] border border-[#1a1a1a]/10 px-4 py-3.5 text-[#1a1a1a] placeholder:text-[#1a1a1a]/40 focus:border-[#1e2a5a] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1e2a5a]/10 text-base transition-all";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold tracking-wide uppercase text-[#1a1a1a]/60 mb-2">
        {label}{required && <span className="text-[#b4462a]"> *</span>}
      </div>
      {children}
    </label>
  );
}
